/* eslint-disable sonarjs/no-duplicate-string */
import { useQueryClient } from '@tanstack/react-query';

import {
  useDeletePlotOverviewMediaFile,
  usePatchPlotOverviewMediaFile,
  usePostPlotOverviewMediaFile,
} from '@/api/rest/resources/plot';
import {
  PatchPlotOverviewMediaFileRequestBodyData,
  PlotOverviewMediaFile,
  PlotOverviewMediaFileType,
  PostPlotOverviewMediaFileRequestBodyData,
} from '@/api/rest/resources/types/plot';
import { Logger } from '@/lib/logs/logger';
import { assertIsDefined } from '@/utils/assert';

export const useCreatePlotOverviewMediaFile = () => {
  const postMutation = usePostPlotOverviewMediaFile();
  const queryClient = useQueryClient();

  const submit = async (
    {
      name,
      caption,
      type,
      fileInfoId,
      thumbnailFileInfoId,
      plotOverviewId,
    }: {
      name: string;
      caption?: string;
      type: PlotOverviewMediaFileType;
      fileInfoId: string;
      thumbnailFileInfoId?: string;
      plotOverviewId: string;
    },
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ) => {
    assertIsDefined(name, 'name');
    assertIsDefined(type, 'type');
    assertIsDefined(fileInfoId, 'fileInfoId');
    assertIsDefined(plotOverviewId, 'plotOverviewId');

    const data: PostPlotOverviewMediaFileRequestBodyData = {
      name,
      caption,
      type,
      file_info: fileInfoId,
      thumbnail_file_info: thumbnailFileInfoId,
      plot_overview: plotOverviewId,
    };

    await postMutation.mutateAsync(
      {
        pathVariables: undefined,
        queryParams: undefined,
        bodyData: data,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['plot', 'plot-overview'] });
          onSuccess?.();
        },
        onError: (error) => {
          Logger.error(`useCreatePlotOverviewMediaFile: ${error}`);
          onError?.(error);
        },
      },
    );
  };

  const isSubmitting = postMutation.isPending;

  return { submit, isSubmitting };
};

export const useRemovePlotOverviewMediaFile = () => {
  const deleteMediaFileIdMutation = useDeletePlotOverviewMediaFile();
  const queryClient = useQueryClient();

  const submit = async (mediaFileId: string, onSuccess?: () => void, onError?: (error: Error) => void) => {
    assertIsDefined(mediaFileId, 'mediaFileId');

    await deleteMediaFileIdMutation.mutateAsync(
      {
        pathVariables: {
          id: mediaFileId,
        },
        queryParams: undefined,
      },
      {
        onSuccess: () => {
          queryClient.resetQueries({ queryKey: ['plot', 'plot-overview'] });
          onSuccess?.();
        },
        onError: (error) => {
          onError?.(error);
        },
      },
    );
  };

  const isSubmitting = deleteMediaFileIdMutation.isPending;

  return { submit, isSubmitting };
};

export const useUpdatePlotOverviewMediaFile = () => {
  const patchPlotOverviewMutation = usePatchPlotOverviewMediaFile();
  const queryClient = useQueryClient();

  const submit = async (
    mediaFile: Partial<PlotOverviewMediaFile>,
    onSuccess?: () => void,
    onError?: (error: Error) => void,
  ) => {
    assertIsDefined(mediaFile.id, 'mediaFile.id');

    const data: PatchPlotOverviewMediaFileRequestBodyData = {};
    if (mediaFile.name) {
      data.name = mediaFile.name;
    }
    if (mediaFile.caption) {
      data.caption = mediaFile.caption;
    }

    if (mediaFile.thumbnail_file_info_id) {
      data.thumbnail_file_info = mediaFile.thumbnail_file_info_id;
    }

    if (!data.name && !data.caption && !data.thumbnail_file_info) {
      throw new Error(
        'At least one of name, caption, or thumbnail_file_info must be provided to update a plot overview media file',
      );
    }

    await patchPlotOverviewMutation.mutateAsync(
      {
        pathVariables: {
          id: mediaFile.id,
        },
        queryParams: undefined,
        bodyData: data,
      },
      {
        onSuccess: () => {
          queryClient.resetQueries({ queryKey: ['plot', 'plot-overview'] });
          onSuccess?.();
        },
        onError: (error) => {
          onError?.(error);
        },
      },
    );
  };

  const isSubmitting = patchPlotOverviewMutation.isPending;

  return { submit, isSubmitting };
};
